import { ColorModeContext, useMode } from "./theme";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { Suspense, lazy } from "react";
import Topbar from "./scenes/global/Topbar.jsx";
import Sidebar from "./scenes/global/Sidebar.jsx";
import { Routes, Route, useLocation } from "react-router-dom";
import Login from "./scenes/login/index.jsx";
import { useInactivityTimeout } from "./hooks/useInactivityTimeout";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy load all route components
const Dashboard = lazy(() => import("./scenes/dashboard"));
const Team = lazy(() => import("./scenes/team"));
const Contacts = lazy(() => import("./scenes/contacts"));
const Bar = lazy(() => import("./scenes/bar"));
const Form = lazy(() => import("./scenes/form"));
const Line = lazy(() => import("./scenes/line"));
const Pie = lazy(() => import("./scenes/pie"));
const FAQ = lazy(() => import("./scenes/faq"));
const Geography = lazy(() => import("./scenes/geography"));
const Calendar = lazy(() => import("./scenes/calendar"));
const Wrapper = lazy(() => import("./scenes/wrapper/index.jsx"));
const SalesObtained = lazy(() => import("./scenes/sales-obtained"));
const LotteryResults = lazy(() => import("./scenes/lottery-results"));
const ManualDraw = lazy(() => import("./scenes/manual-draw"));
const UserSales = lazy(() => import("./scenes/users-sales"));
const Groups = lazy(() => import("./scenes/groups"));
const CreateGroup = lazy(() => import("./scenes/groups/CreateGroup"));
const NumberVolume = lazy(() => import("./scenes/number-volume"));
const WhatsAppImport = lazy(() => import("./scenes/whatsapp-import"));
const TrainingExamples = lazy(() => import("./scenes/training-examples"));
const PhotoReview = lazy(() => import("./scenes/photo-review"));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    Loading...
  </div>
);

function App() {
  const [theme, colorMode] = useMode();
  const location = useLocation();
  useInactivityTimeout();

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="app">
          {location.pathname !== "/" && <Sidebar />}
          <main className="content">
            {location.pathname !== "/" && <Topbar />}
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/dashboard" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Wrapper><Dashboard /></Wrapper></Suspense></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Team /></Suspense></ProtectedRoute>} />
              <Route path="/contacts" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Contacts /></Suspense></ProtectedRoute>} />
              <Route path="/sales-obtained" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><SalesObtained /></Suspense></ProtectedRoute>} />
              <Route path="/user-sales" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><UserSales /></Suspense></ProtectedRoute>} />
              <Route path="/lottery-results" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><LotteryResults /></Suspense></ProtectedRoute>} />
              <Route path="/manual-draw" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><ManualDraw /></Suspense></ProtectedRoute>} />
              <Route path="/groups" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Groups /></Suspense></ProtectedRoute>} />
              <Route path="/groups/create" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><CreateGroup /></Suspense></ProtectedRoute>} />
              <Route path="/groups/edit/:id" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><CreateGroup /></Suspense></ProtectedRoute>} />
              <Route path="/number-volume" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><NumberVolume /></Suspense></ProtectedRoute>} />
              <Route path="/whatsapp-import" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><WhatsAppImport /></Suspense></ProtectedRoute>} />
              <Route path="/training-examples" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><TrainingExamples /></Suspense></ProtectedRoute>} />
              <Route path="/photo-review" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><PhotoReview /></Suspense></ProtectedRoute>} />
              <Route path="/form" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Form /></Suspense></ProtectedRoute>} />
              <Route path="/bar" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Bar /></Suspense></ProtectedRoute>} />
              <Route path="/pie" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Pie /></Suspense></ProtectedRoute>} />
              <Route path="/line" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Line /></Suspense></ProtectedRoute>} />
              <Route path="/faq" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><FAQ /></Suspense></ProtectedRoute>} />
              <Route path="/geography" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Geography /></Suspense></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Calendar /></Suspense></ProtectedRoute>} />
            </Routes>
          </main>
        </div>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
